import React, { useState, useEffect, useRef, useCallback } from "react";
import { VoiceVisualizer } from "./AudioVisualizer";
import { BsMicFill, BsMicMuteFill } from "react-icons/bs";
import { IoArrowBack } from "react-icons/io5";
import {
  HiOutlineChat,
  HiPhone,
  HiOutlineShare,
  HiCheck,
} from "react-icons/hi";
import { FaHistory } from "react-icons/fa";
import { MdOutlineAutorenew } from "react-icons/md";
import { IoMdClose, IoMdExpand, IoMdContract } from "react-icons/io";
import { IconWrapper } from "./IconWrapper";
import {
  API_BASE_URL,
  AGENT_ENDPOINTS,
  LANGUAGE_ENDPOINTS,
  ADMIN_AGENT_ENDPOINTS,
} from "../../config/api";
import {
  AgentSummary,
  LanguageMapping,
  initiatePhoneCall,
} from "../../services/api";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../services/api";
import { Modal } from "../Common/Modal";
import { validateIndianPhone, formatIndianPhone } from "../../utils/validation";
import PhoneInput from "../Common/PhoneInput";
import { motion } from "framer-motion";

// Cost constants for Gemini Live API (in USD per 1M tokens)
const COST_CONSTANTS = {
  // Gemini costs
  GEMINI_INPUT_TEXT: 0.5,
  GEMINI_INPUT_AUDIO: 1,
  GEMINI_OUTPUT_TEXT: 2.5,
  GEMINI_OUTPUT_AUDIO: 12.5,
  USD_TO_INR: 86, // 1 USD = 86 INR

  // Fixed costs (in INR)
  FIXED_CONNECTION_COST: 2, // Fixed cost per connection (INR)
  CALLING_SERVICE_COST_PER_MIN: 1, // Calling service cost per minute (INR)
  SERVICE_CHARGE_PER_MIN: 0.3, // Service charge in INR per minute (flat rate)
};

interface AgentAccessResponse {
  remaining_uses: number | null;
  agent_info: AgentSummary;
  access_token?: string;
}

interface AudioAgentProps {
  apiPath?: string;
  onBackToSelection?: () => void;
  onConnectionChange?: (connected: boolean) => void;
}

// Define token usage interface
interface TokenUsage {
  input_tokens: number;
  output_tokens: number;
  input_token_details: {
    text: number;
    audio: number;
    cached: number;
    cached_text: number;
    cached_audio: number;
  };
  output_token_details: {
    text: number;
    audio: number;
  };
}

// Define chat message interface
interface ChatMessage {
  type: "user" | "agent";
  text: string;
  timestamp: number;
}

// Declare the global window interface extension
declare global {
  interface Window {
    updateRemainingUses?: (uses: number | null) => void;
  }
}

const AudioAgent: React.FC<AudioAgentProps> = ({
  apiPath,
  onBackToSelection,
  onConnectionChange,
}) => {
  const { uuid: paramAgentUuid } = useParams<{ uuid: string }>();
  const agentUuid = paramAgentUuid || "";
  const navigate = useNavigate();

  const [agent, setAgent] = useState<AgentSummary | null>(null);
  const [agentAccessToken, setAgentAccessToken] = useState<string | undefined>(
    undefined
  );
  const [remainingUses, setRemainingUses] = useState<number | null>(null);
  const [loadingAgent, setLoadingAgent] = useState<boolean>(true);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [outputStream, setOutputStream] = useState<MediaStream | null>(null);
  const [audioActive, setAudioActive] = useState<boolean>(false);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [languageMappings, setLanguageMappings] = useState<Map<string, string>>(
    new Map()
  );

  // Live chat UI state
  const [chatExpanded, setChatExpanded] = useState<boolean>(false);
  const [showChat, setShowChat] = useState<boolean>(true);

  // Live chat messages
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);

  // Token usage tracking
  const [tokenUsage, setTokenUsage] = useState<TokenUsage>({
    input_tokens: 0,
    output_tokens: 0,
    input_token_details: {
      text: 0,
      audio: 0,
      cached: 0,
      cached_text: 0,
      cached_audio: 0,
    },
    output_token_details: {
      text: 0,
      audio: 0,
    },
  });

  // Additional costs tracking
  const [callDurationMinutes, setCallDurationMinutes] = useState<number>(0);
  const [fixedCostsApplied, setFixedCostsApplied] = useState<boolean>(false);

  // Calculated costs
  const [costs, setCosts] = useState({
    usd: 0,
    inr: 0,
    fixedCost: 0,
    callingServiceCost: 0,
    serviceCharge: 0,
    totalInr: 0,
  });

  // Show cost breakdown modal
  const [showCostModal, setShowCostModal] = useState<boolean>(false);
  // Session timer
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [sessionDuration, setSessionDuration] = useState<number>(0);
  const timerIntervalRef = useRef<number | null>(null);

  // Phone call mode states
  const [phoneCallMode, setPhoneCallMode] = useState<boolean>(false);
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [phoneNumberPlaceholder, setPhoneNumberPlaceholder] =
    useState<string>("");
  const [isPhoneCallProcessing, setIsPhoneCallProcessing] =
    useState<boolean>(false);
  const [phoneCallMessage, setPhoneCallMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [phoneValidationMsg, setPhoneValidationMsg] = useState<string | null>(
    null
  );

  // Phone call warning
  const [showPhoneCallWarning, setShowPhoneCallWarning] =
    useState<boolean>(false);

  // Add state for out of uses modal
  const [showOutOfUsesModal, setShowOutOfUsesModal] = useState<boolean>(false);

  // Share state
  const [linkCopied, setLinkCopied] = useState<boolean>(false);

  const audioRef = useRef<HTMLAudioElement>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const webrtcIdRef = useRef<string>("");

  // Web Audio refs for mic processing and remote analysis
  const audioContextRef = useRef<AudioContext | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const remoteAnalyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  // Keep track if mic gain is lowered
  const micLowRef = useRef<boolean>(false);

  const isAdmin = localStorage.getItem("userType") === "admin";
  // Auto-scroll to the bottom when new messages are added
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // Check if screen is mobile using useEffect to avoid hydration issues
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint is 1024px
    };

    // Initial check
    checkIfMobile();

    // Add event listener for window resize
    window.addEventListener("resize", checkIfMobile);

    // Cleanup
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  // Set default chat state based on screen size
  useEffect(() => {
    // On desktop: always show expanded chat
    // On mobile: hide chat initially, show button
    setShowChat(!isMobile);
    setChatExpanded(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  // Clear chat messages when disconnecting
  useEffect(() => {
    if (!isConnected) {
      // Don't clear messages immediately so user can still see them after disconnecting
      // Only clear when connecting again
      if (isConnecting) {
        setChatMessages([]);
      }
    }
  }, [isConnecting, isConnected]);

  // Timer display component
  const TimerDisplay = () => {
    // Force rerenders every second using a counter
    const [, forceUpdate] = useState({});

    useEffect(() => {
      const timer = setInterval(() => {
        forceUpdate({});
      }, 500);

      return () => clearInterval(timer);
    }, []);

    return (
      <div className="bg-[var(--card)] text-[var(--foreground)] font-semibold px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-2 border border-[var(--border)] shadow-sm">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span className="min-w-[40px] inline-block text-center">
          {formatDuration(sessionDuration)}
        </span>
      </div>
    );
  };

  // Add this component to force updates for modal session duration display
  const SessionDurationDisplay = () => {
    // Force rerenders every second
    const [, forceUpdate] = useState({});

    useEffect(() => {
      const timer = setInterval(() => {
        forceUpdate({});
      }, 500);

      // Clean up the interval when component unmounts
      return () => clearInterval(timer);
    }, []);

    return (
      <p className="text-gray-900 font-medium">
        {formatDuration(sessionDuration)}
      </p>
    );
  };

  // Fetch language mappings
  useEffect(() => {
    const fetchLanguageMappings = async () => {
      try {
        const response = await api.get(LANGUAGE_ENDPOINTS.MAPPINGS);
        const mappingsData: LanguageMapping[] = response.data;
        const mappings = new Map<string, string>();
        mappingsData.forEach((mapping) => {
          mappings.set(mapping.language_code, mapping.language);
        });
        setLanguageMappings(mappings);
      } catch (error) {
        console.error("Error fetching language mappings:", error);
      }
    };

    fetchLanguageMappings();
  }, []);

  // First thing: validate agent access and redirect immediately if invalid
  useEffect(() => {
    const validateAgentAccess = async () => {
      if (!agentUuid) {
        // No agent UUID means direct redirect
        if (onBackToSelection) {
          onBackToSelection();
        } else {
          navigate("/");
        }
        return;
      }

      try {
        setLoadingAgent(true);

        const response = await api.post(AGENT_ENDPOINTS.ACCESS(agentUuid));
        const data: AgentAccessResponse = response.data;

        // Log access token received from the API
        console.log("Access token received from API:", data.access_token);
        console.log(
          "Access token from agent_info:",
          data.agent_info?.access_token
        );

        // Valid agent, set the data
        setAgent(data.agent_info);
        // Store access token from agent_info if provided
        setAgentAccessToken(data.agent_info?.access_token);

        // Extract phone number from token data if available
        try {
          const token = localStorage.getItem("token");
          if (token) {
            const tokenPayload = JSON.parse(atob(token.split(".")[1]));
            if (tokenPayload && tokenPayload.phone) {
              // Remove +91 prefix if present
              const phoneNum = tokenPayload.phone.replace(/^\+91/, "");
              setPhoneNumberPlaceholder(phoneNum);
            }
          }
        } catch (err) {
          console.error("Error extracting phone number from token:", err);
        }

        setRemainingUses(data.remaining_uses);
        // Also update in localStorage for the navbar
        if (data.remaining_uses !== null && data.remaining_uses !== undefined) {
          localStorage.setItem("remainingUses", data.remaining_uses.toString());
          // Update the global counter
          if (window.updateRemainingUses) {
            window.updateRemainingUses(data.remaining_uses);
          }
        }
        setLoadingAgent(false);
      } catch (error: any) {
        console.error("Error validating agent access:", error);

        // Check if it's a 401 Unauthorized error (out of uses)
        if (error.response && error.response.status === 401) {
          // Show the out of uses modal
          setShowOutOfUsesModal(true);
          setLoadingAgent(false);
          // Don't redirect immediately, let the user see the modal
          return;
        }

        // For other errors, redirect
        if (onBackToSelection) {
          onBackToSelection();
        } else {
          navigate("/");
        }
      }
    };

    // Make this the first thing that happens
    validateAgentAccess();
  }, [agentUuid, navigate, onBackToSelection]);

  // Get language name from code
  const getLanguageName = (code: string): string => {
    return languageMappings.get(code) || code;
  };

  // Notify parent of connection state changes
  useEffect(() => {
    onConnectionChange?.(isConnected);
  }, [isConnected, onConnectionChange]);

  // Define a decay duration in milliseconds (e.g., 500ms)
  const DECAY_DURATION = 500;
  // Track the last time when the remote output was above threshold
  let lastActiveTime = performance.now();

  const checkRemoteOutputLevel = () => {
    if (remoteAnalyserRef.current && gainNodeRef.current) {
      const analyser = remoteAnalyserRef.current;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(dataArray);
      // Calculate simple average volume
      const avg =
        dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
      const threshold = 10; // adjust this threshold as needed
      const now = performance.now();

      if (avg > threshold) {
        // Record the current time as the last moment of activity.
        lastActiveTime = now;
        // Lower mic gain if not already lowered
        if (!micLowRef.current) {
          gainNodeRef.current.gain.value = 0.3; // Lower gain value
          micLowRef.current = true;
        }
      } else if (micLowRef.current) {
        // Only restore gain if enough time has passed without activity.
        if (now - lastActiveTime > DECAY_DURATION) {
          gainNodeRef.current.gain.value = 1.0;
          micLowRef.current = false;
        }
      }
    }
    animationFrameRef.current = requestAnimationFrame(checkRemoteOutputLevel);
  };

  // Setup remote analyser for the remote output stream
  const setupRemoteAnalyser = (remoteStream: MediaStream) => {
    // Removed as of now as we are not using standard mode
    return;
    const ac = audioContextRef.current || new AudioContext();
    if (!audioContextRef.current) {
      audioContextRef.current = ac;
    }
    const remoteSource = ac.createMediaStreamSource(remoteStream);
    const analyser = ac.createAnalyser();
    analyser.fftSize = 256; // smaller fftSize for lower latency in analysis
    remoteSource.connect(analyser);
    remoteAnalyserRef.current = analyser;
    // Start monitoring
    if (!animationFrameRef.current) {
      animationFrameRef.current = requestAnimationFrame(checkRemoteOutputLevel);
    }
  };

  // Setup microphone with gain processing
  const setupMicrophone = async () => {
    try {
      // Get the microphone stream
      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000,
          channelCount: 1,
        },
      });

      // Create an AudioContext and process the mic stream through a GainNode
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      const micSource = audioContext.createMediaStreamSource(micStream);
      const gainNode = audioContext.createGain();
      gainNode.gain.value = 1.0; // start with full gain
      gainNodeRef.current = gainNode;
      micSource.connect(gainNode);
      const destination = audioContext.createMediaStreamDestination();
      gainNode.connect(destination);

      // Use the processed stream for further WebRTC processing
      setStream(destination.stream);
      setErrorMessage("");
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      setErrorMessage(`Failed to access microphone: ${errorMsg}`);
      console.error("Microphone setup failed:", errorMsg);
    }
  };

  // Add this effect to ensure timer updates properly and track call duration
  useEffect(() => {
    // Only start the timer when both sessionStartTime exists AND we're connected
    if (isConnected && !timerIntervalRef.current) {
      // Reset the session start time to now when we actually get connected
      const newStartTime = Date.now();
      setSessionStartTime(newStartTime);
      // Reset duration to zero
      setSessionDuration(0);
      setCallDurationMinutes(0);

      // Apply fixed costs when connection is established
      if (!fixedCostsApplied) {
        setCosts((prevCosts) => ({
          ...prevCosts,
          fixedCost: COST_CONSTANTS.FIXED_CONNECTION_COST,
          totalInr: prevCosts.inr + COST_CONSTANTS.FIXED_CONNECTION_COST,
          totalUsd:
            (prevCosts.inr + COST_CONSTANTS.FIXED_CONNECTION_COST) /
            COST_CONSTANTS.USD_TO_INR,
        }));
        setFixedCostsApplied(true);
      }

      timerIntervalRef.current = window.setInterval(() => {
        const currentDuration = Math.floor((Date.now() - newStartTime) / 1000);
        setSessionDuration(currentDuration);

        // Calculate minutes for billing (ceiling to the next minute)
        const minutes = Math.ceil(currentDuration / 60);
        if (minutes > callDurationMinutes) {
          setCallDurationMinutes(minutes);

          // Update calling service cost and service charge
          const callingServiceCost =
            minutes * COST_CONSTANTS.CALLING_SERVICE_COST_PER_MIN;
          const serviceCharge = minutes * COST_CONSTANTS.SERVICE_CHARGE_PER_MIN;

          setCosts((prevCosts) => ({
            ...prevCosts,
            callingServiceCost,
            serviceCharge,
            totalInr:
              prevCosts.inr +
              callingServiceCost +
              serviceCharge +
              COST_CONSTANTS.FIXED_CONNECTION_COST,
            totalUsd:
              (prevCosts.inr +
                COST_CONSTANTS.FIXED_CONNECTION_COST +
                callingServiceCost +
                serviceCharge) /
              COST_CONSTANTS.USD_TO_INR,
          }));
        }
      }, 1000);
    } else if (!isConnected && timerIntervalRef.current) {
      // Stop the timer if we disconnect
      window.clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    // Cleanup function to clear timer when component unmounts
    return () => {
      if (timerIntervalRef.current) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [isConnected, fixedCostsApplied, callDurationMinutes]);

  // Wait for ICE gathering to complete (from working HTML example)
  const waitForIceGatheringComplete = async (pc: RTCPeerConnection, timeoutMs: number = 2000): Promise<void> => {
    if (pc.iceGatheringState === 'complete') return;
    console.log("Waiting for ICE gathering to complete. Current state:", pc.iceGatheringState);
    return new Promise((resolve) => {
      let timeoutId: number;
      const checkState = () => {
        console.log("icegatheringstatechange:", pc.iceGatheringState);
        if (pc.iceGatheringState === 'complete') {
          cleanup();
          resolve();
        }
      };
      const onTimeout = () => {
        console.warn(`ICE gathering timed out after ${timeoutMs} ms.`);
        cleanup();
        resolve();
      };
      const cleanup = () => {
        pc.removeEventListener('icegatheringstatechange', checkState);
        clearTimeout(timeoutId);
      };
      pc.addEventListener('icegatheringstatechange', checkState);
      timeoutId = window.setTimeout(onTimeout, timeoutMs);
      // Checking the state again to avoid any eventual race condition
      checkState();
    });
  };

  // Connect to voice agent using SmallWebRTC compatible approach
  const connectToAgent = async () => {
    // Double check we have a microphone stream
    if (!stream) {
      setErrorMessage("Please enable microphone first");
      return;
    }

    setErrorMessage("");
    setIsConnecting(true);

    // Reset token usage and costs when connecting
    setSessionDuration(0);
    setCallDurationMinutes(0);
    resetCostCounter();

    try {
      // Create new RTCPeerConnection with reliable STUN/TURN servers
      const isProduction = window.location.hostname !== 'localhost';
      const pc = new RTCPeerConnection({
        iceServers: [
          // Google's reliable public STUN servers
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          // Fallback AWS TURN server (if accessible)
          {
            "urls": ["stun:35.200.136.192:3478", "turn:35.200.136.192:3478"],
            "username": "uktiuser",
            "credential": "uktipass"
          }
        ],
        iceCandidatePoolSize: isProduction ? 10 : 0,
        iceTransportPolicy: "all", // Allow both direct and relay connections
        bundlePolicy: "max-bundle",
        rtcpMuxPolicy: "require"
      });
      peerConnectionRef.current = pc;

      // Setup connection state handlers
      pc.onconnectionstatechange = () => {
        console.log("onconnectionstatechange", pc?.connectionState);
        if (pc.connectionState === "connected") {
          setIsConnected(true);
          setIsConnecting(false);
        } else if (
          pc.connectionState === "disconnected" ||
          pc.connectionState === "failed" ||
          pc.connectionState === "closed"
        ) {
          setIsConnected(false);
          setAudioActive(false);
          setIsConnecting(false);
        }
      };

      pc.oniceconnectionstatechange = () => {
        console.log("oniceconnectionstatechange", pc?.iceConnectionState);
        
        // Handle ICE connection failures with restart logic
        if (pc?.iceConnectionState === 'failed') {
          console.log("ICE connection failed, attempting restart...");
          // Trigger ICE restart
          pc.restartIce();
        } else if (pc?.iceConnectionState === 'disconnected') {
          console.log("ICE connection disconnected, monitoring for recovery...");
          // Set a timeout to restart if not recovered
          setTimeout(() => {
            if (pc?.iceConnectionState === 'disconnected') {
              console.log("ICE still disconnected after timeout, restarting...");
              pc.restartIce();
            }
          }, 5000);
        }
      };

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          console.log("New ICE candidate:", event.candidate);
          console.log(`ICE candidate type: ${event.candidate.type}, protocol: ${event.candidate.protocol}, address: ${event.candidate.address || 'N/A'}`);
        } else {
          console.log("All ICE candidates have been sent.");
        }
      };

      // Handle incoming tracks (for audio output)
      pc.ontrack = (evt) => {
        console.log("Received remote track:", evt.track.kind);
        if (audioRef.current && audioRef.current.srcObject !== evt.streams[0]) {
          audioRef.current.srcObject = evt.streams[0];
          setOutputStream(evt.streams[0]);
          setAudioActive(true);
          // Setup analyser for remote output to control mic gain
          setupRemoteAnalyser(evt.streams[0]);
        }
      };

      // SmallWebRTC expects both audio and video transceivers
      const audioTrack = stream.getAudioTracks()[0];
      pc.addTransceiver(audioTrack, { direction: 'sendrecv' });
      pc.addTransceiver('video', { direction: 'sendrecv' });

      // Create data channel for text communication
      const dataChannel = pc.createDataChannel("text");
      dataChannelRef.current = dataChannel;

      // Setup data channel handlers
      dataChannel.onopen = () => {
        console.log("Data channel opened");
      };

      dataChannel.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === "disconnected") {
            console.log("Disconnecting from agent");
            disconnectFromAgent();
          }
          // Process token usage for Gemini mode
          if (message.type === "token_usage") {
            // Extract token counts from Gemini format
            const inputText = message.data.input?.text || 0;
            const inputAudio = message.data.input?.audio || 0;
            const outputText = message.data.output?.text || 0;
            const outputAudio = message.data.output?.audio || 0;

            // Update token usage
            setTokenUsage((prevUsage) => {
              // For Gemini, we need to accumulate the counts
              const newInputText =
                prevUsage.input_token_details.text + inputText;
              const newInputAudio =
                prevUsage.input_token_details.audio + inputAudio;
              const newOutputText =
                prevUsage.output_token_details.text + outputText;
              const newOutputAudio =
                prevUsage.output_token_details.audio + outputAudio;

              // Calculate total tokens
              const totalInputTokens = newInputText + newInputAudio;
              const totalOutputTokens = newOutputText + newOutputAudio;

              const newUsage = {
                input_tokens: totalInputTokens,
                output_tokens: totalOutputTokens,
                input_token_details: {
                  text: newInputText,
                  audio: newInputAudio,
                  cached: 0, // Gemini doesn't have cached tokens concept
                  cached_text: 0,
                  cached_audio: 0,
                },
                output_token_details: {
                  text: newOutputText,
                  audio: newOutputAudio,
                },
              };

              // Calculate costs in USD and INR using Gemini rates
              const textInputCost =
                (newUsage.input_token_details.text / 1000000) *
                COST_CONSTANTS.GEMINI_INPUT_TEXT;
              const audioInputCost =
                (newUsage.input_token_details.audio / 1000000) *
                COST_CONSTANTS.GEMINI_INPUT_AUDIO;
              const textOutputCost =
                (newUsage.output_token_details.text / 1000000) *
                COST_CONSTANTS.GEMINI_OUTPUT_TEXT;
              const audioOutputCost =
                (newUsage.output_token_details.audio / 1000000) *
                COST_CONSTANTS.GEMINI_OUTPUT_AUDIO;

              const totalUsdCost =
                textInputCost +
                audioInputCost +
                textOutputCost +
                audioOutputCost;
              const totalInrCost = totalUsdCost * COST_CONSTANTS.USD_TO_INR;

              // Update costs
              setCosts((prevCosts) => ({
                usd: totalUsdCost,
                inr: totalInrCost,
                fixedCost: prevCosts.fixedCost,
                callingServiceCost: prevCosts.callingServiceCost,
                serviceCharge: prevCosts.serviceCharge,
                totalInr:
                  totalInrCost +
                  prevCosts.fixedCost +
                  prevCosts.callingServiceCost +
                  prevCosts.serviceCharge,
              }));

              return newUsage;
            });
          }

          // Handle errors from the backend
          if (message.type === "error" && message.data?.message) {
            console.error("Error from backend:", message.data.message);

            // Handle file not found errors specifically
            if (
              message.data.message.includes("not found") ||
              message.data.message.includes("404") ||
              message.data.message.includes("no such file")
            ) {
              setErrorMessage(
                "Audio file not found. The recording may have been deleted or is unavailable."
              );
            } else {
              setErrorMessage(message.data.message);
            }
          }

          // Capture user transcriptions
          if (message.type === "user_transcription" && message.data?.text) {
            setChatMessages((prev) => {
              const newMessages = [
                ...prev,
                {
                  type: "user" as const,
                  text: message.data.text,
                  timestamp: Date.now(),
                },
              ];
              return newMessages;
            });
          }

          // Capture agent responses
          if (message.type === "output_transcription" && message.data?.text) {
            setChatMessages((prev) => {
              const newMessages = [
                ...prev,
                {
                  type: "agent" as const,
                  text: message.data.text,
                  timestamp: Date.now(),
                },
              ];
              return newMessages;
            });
          }
        } catch (e) {
          console.error("Error parsing data channel message:", e);
          console.log("Received raw message:", event.data);
        }
      };

      // Create offer and wait for ICE gathering to complete
      await pc.setLocalDescription(await pc.createOffer());
      await waitForIceGatheringComplete(pc);

      // Construct the full WebRTC endpoint URL
      const path = apiPath || `/agents/${agentUuid}`;
      const webrtcEndpoint = `${API_BASE_URL}${path}/offer`;

      const token = localStorage.getItem("token");
      
      // Generate a unique connection ID for this session to avoid reusing closed connections
      const connectionId = `pc_${agentUuid}_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      
      // Send offer to server (no individual ICE candidates needed for SmallWebRTC)
      const response = await fetch(webrtcEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sdp: pc.localDescription?.sdp,
          type: pc.localDescription?.type,
          pc_id: connectionId,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Handle server response
      const serverResponse = await response.json();
      await pc.setRemoteDescription(serverResponse);

    } catch (error) {
      console.error("Error connecting:", error);
      const errorMsg =
        error instanceof Error
          ? error.message
          : "Failed to connect to voice agent";
      setErrorMessage(errorMsg);
      setAudioActive(false);
      setIsConnecting(false);

      // Clean up failed connection
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }
    }
  };

  // Toggle mute
  const toggleMute = () => {
    if (stream) {
      stream.getAudioTracks().forEach((track) => {
        track.enabled = !track.enabled;
      });
      setIsMuted(!isMuted);
    }
  };

  // Helper function to format duration in HH:MM:SS
  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    const pad = (num: number) => num.toString().padStart(2, "0");

    if (hours > 0) {
      return `${pad(hours)}:${pad(minutes)}:${pad(remainingSeconds)}`;
    } else {
      return `${pad(minutes)}:${pad(remainingSeconds)}`;
    }
  };

  // Reset token usage and costs
  const resetCostCounter = () => {
    setTokenUsage({
      input_tokens: 0,
      output_tokens: 0,
      input_token_details: {
        text: 0,
        audio: 0,
        cached: 0,
        cached_text: 0,
        cached_audio: 0,
      },
      output_token_details: { text: 0, audio: 0 },
    });
    setCosts({
      usd: 0,
      inr: 0,
      fixedCost: 0,
      callingServiceCost: 0,
      serviceCharge: 0,
      totalInr: 0,
    });
    setFixedCostsApplied(false);
    setCallDurationMinutes(0);
  };

  // Add this function to track if a session was meaningful
  const wasSessionMeaningful = () => {
    // Check if session duration was meaningful (more than 5 seconds)
    if (sessionDuration > 5) return true;

    // Or if there were any chat messages exchanged
    if (chatMessages.length > 0) return true;

    return false;
  };

  // Disconnect from voice agent
  const disconnectFromAgent = async () => {
    try {
      setErrorMessage(null);

      // Check if the session was meaningful before we change any state
      const shouldDeductUse = wasSessionMeaningful();

      // First set UI state to prevent user interaction during disconnect
      setIsConnected(false);
      setAudioActive(false);
      setOutputStream(null);
      setIsConnecting(false);

      // Stop the session timer
      if (timerIntervalRef.current) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }

      if (!agentUuid) {
        setErrorMessage("No active agent found");
        return;
      }

      // Decrement remaining uses counter after successful call
      if (remainingUses !== null && !isAdmin && shouldDeductUse) {
        const newRemainingUses = Math.max(0, remainingUses - 1);
        setRemainingUses(newRemainingUses);

        // Update localStorage
        localStorage.setItem("remainingUses", newRemainingUses.toString());

        // Update the global counter if available
        if (window.updateRemainingUses) {
          window.updateRemainingUses(newRemainingUses);
        }
      }

      // Cancel remote level monitoring if running
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      // Stop all tracks in the input stream
      if (stream) {
        stream.getTracks().forEach((track) => {
          track.stop();
        });
        setStream(null);
      }

      // Close the peer connection
      if (peerConnectionRef.current) {
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }

      // Close data channel
      if (dataChannelRef.current) {
        dataChannelRef.current.close();
        dataChannelRef.current = null;
      }

      // Clean up audio element
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.srcObject = null;
        } catch (e) {
          console.error("Error cleaning up audio element:", e);
        }
      }
    } catch (error) {
      console.error("Error disconnecting:", error);
      setErrorMessage("Error during disconnection. Please refresh the page.");
    }
  };

  // Toggle cost breakdown modal
  const toggleCostModal = () => {
    setShowCostModal(!showCostModal);
  };

  // Calculate detailed costs for display
  const calculateDetailedCosts = () => {
    if (!tokenUsage.input_token_details || !tokenUsage.output_token_details) {
      return {
        textInputCost: 0,
        audioInputCost: 0,
        textOutputCost: 0,
        audioOutputCost: 0,
        totalUsdCost: 0,
        totalInrCost: 0,
        perMinuteUsdCost: 0,
        perMinuteInrCost: 0,
        fixedCost: costs.fixedCost,
        callingServiceCost: costs.callingServiceCost,
        serviceCharge: costs.serviceCharge,
        grandTotalInr: costs.totalInr,
        grandTotalUsd: costs.totalInr / COST_CONSTANTS.USD_TO_INR,
      };
    }

    const textInputCost =
      (tokenUsage.input_token_details.text / 1000000) *
      COST_CONSTANTS.GEMINI_INPUT_TEXT;
    const audioInputCost =
      (tokenUsage.input_token_details.audio / 1000000) *
      COST_CONSTANTS.GEMINI_INPUT_AUDIO;
    const textOutputCost =
      (tokenUsage.output_token_details.text / 1000000) *
      COST_CONSTANTS.GEMINI_OUTPUT_TEXT;
    const audioOutputCost =
      (tokenUsage.output_token_details.audio / 1000000) *
      COST_CONSTANTS.GEMINI_OUTPUT_AUDIO;

    const totalUsdCost =
      textInputCost + audioInputCost + textOutputCost + audioOutputCost;
    const totalInrCost = totalUsdCost * COST_CONSTANTS.USD_TO_INR;

    // Calculate the grand total including fixed and service costs
    const grandTotalInr =
      totalInrCost +
      costs.fixedCost +
      costs.callingServiceCost +
      costs.serviceCharge;

    const grandTotalUsd = grandTotalInr / COST_CONSTANTS.USD_TO_INR;

    // Calculate per-minute costs
    // Use a minimum of 1 minute to avoid division by zero or unrealistic per-minute costs
    const minutes = Math.max(sessionDuration / 60, 1);
    const perMinuteUsdCost = grandTotalUsd / minutes;
    const perMinuteInrCost = grandTotalInr / minutes;

    return {
      textInputCost,
      audioInputCost,
      textOutputCost,
      audioOutputCost,
      totalUsdCost,
      totalInrCost,
      perMinuteUsdCost,
      perMinuteInrCost,
      fixedCost: costs.fixedCost,
      callingServiceCost: costs.callingServiceCost,
      serviceCharge: costs.serviceCharge,
      grandTotalInr,
      grandTotalUsd,
    };
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      try {
        // Clean up audio element
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.srcObject = null;
        }

        // Clean up input stream if it exists
        if (stream) {
          stream.getTracks().forEach((track) => {
            try {
              track.stop();
            } catch (e) {
              console.error(`Error stopping track ${track.kind}:`, e);
            }
          });
        }

        // Close the peer connection
        if (peerConnectionRef.current) {
          peerConnectionRef.current.close();
        }

        // Close data channel
        if (dataChannelRef.current) {
          dataChannelRef.current.close();
        }

        // Cancel any active animation frame for remote monitoring
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }

        // Clean up timer interval
        if (timerIntervalRef.current) {
          window.clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
        }
      } catch (e) {
        console.error("Error during cleanup:", e);
      }
    };
  }, [stream]);

  // Handle back to selection
  const handleBack = () => {
    if (onBackToSelection) {
      onBackToSelection();
    } else {
      navigate("/");
    }
  };

  // Format timestamp for chat messages
  const formatMessageTime = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Handle touch events for swipe controls
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartY === null) return;

    const touchY = e.touches[0].clientY;
    const diff = touchStartY - touchY;

    // If swiping up and chat is minimized, expand it
    if (diff > 50 && !chatExpanded) {
      setChatExpanded(true);
    }
    // If swiping down and chat is expanded, minimize it
    else if (diff < -50 && chatExpanded) {
      setChatExpanded(false);
    }
  };

  const handleTouchEnd = () => {
    setTouchStartY(null);
  };

  // Handle phone number input changes
  const handlePhoneNumberChange = (value: string | undefined) => {
    setPhoneNumber(value || "");
  };

  // Validate phone number
  const validatePhoneNumber = (): boolean => {
    setPhoneValidationMsg(null);

    if (!phoneNumber.trim()) {
      setPhoneValidationMsg("Phone number is required");
      return false;
    }

    if (!validateIndianPhone(phoneNumber)) {
      setPhoneValidationMsg("Please enter a valid Indian phone number");
      return false;
    }

    return true;
  };

  // Handle phone call submission
  const handlePhoneCallSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePhoneNumber()) {
      return;
    }

    setIsPhoneCallProcessing(true);
    setPhoneCallMessage(null);

    try {
      // Format phone number to Indian E.164 format
      const formattedPhone = formatIndianPhone(phoneNumber);

      const result = await initiatePhoneCall(formattedPhone, agentUuid);

      if (result.success) {
        setPhoneCallMessage({
          type: "success",
          text: `Call made to ${formattedPhone}`,
        });
        // Clear phone number after successful submission
        setPhoneNumber("");

        // Decrement remaining uses counter after successful phone call
        if (remainingUses !== null && !isAdmin) {
          const newRemainingUses = Math.max(0, remainingUses - 1);
          setRemainingUses(newRemainingUses);

          // Update localStorage
          localStorage.setItem("remainingUses", newRemainingUses.toString());

          // Update the global counter if available
          if (window.updateRemainingUses) {
            window.updateRemainingUses(newRemainingUses);
          }
        }
      } else {
        setPhoneCallMessage({
          type: "error",
          text:
            result.message ||
            "Failed to initiate phone call. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error initiating phone call:", error);
      setPhoneCallMessage({
        type: "error",
        text: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setIsPhoneCallProcessing(false);
    }
  };

  // Toggle phone call mode
  const togglePhoneCallMode = () => {
    setPhoneCallMode(!phoneCallMode);
    // Reset states when toggling
    setPhoneNumber("");
    setPhoneValidationMsg(null);
    setPhoneCallMessage(null);
  };

  // Handle sharing agent link
  const handleShareAgent = () => {
    // Include access token in the link if available
    let link = `${window.location.origin}/access?agent=${agentUuid}`;

    // Debug log
    console.log("Access token for sharing:", agentAccessToken);

    // Add the access_token to the URL if available
    if (agentAccessToken) {
      link += `&token=${agentAccessToken}`;
      console.log("Final share link:", link);
    } else {
      console.warn("No access token available for sharing");
    }

    navigator.clipboard
      .writeText(link)
      .then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy link: ", err);
      });
  };

  if (loadingAgent) {
    return (
      <div className="min-h-screen bg-[var(--background)] relative">
        <div className="relative z-10 py-12 px-4">
          <div className="max-w-7xl mx-auto flex justify-center items-center py-12">
            <div className="flex flex-col items-center">
              <div className="animate-pulse">
                <HiOutlineChat className="w-16 h-16 text-[var(--color-secondary)]" />
              </div>
              <p className="mt-4 text-[var(--foreground)]">
                Loading agent details...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--background)] relative">
      {/* Main Content */}
      <div className="relative z-10 py-1 px-3 md:py-4 md:px-4 !pt-0">
        <div className="container mx-auto max-w-7xl">
          {agent && (
            <div className="text-center mb-4">
              <h1 className="text-2xl md:text-3xl font-bold text-[var(--foreground)] mb-4 md:mb-6 text-center animate-fade-in">
                {agent.name}
              </h1>
            </div>
          )}

          <div className="flex flex-col lg:flex-row gap-4 md:gap-6">
            {/* Main audio agent panel */}
            <div className="bg-[var(--card-container)] font-semibold border border-[var(--border)] rounded-3xl p-4 md:p-8 shadow-lg flex-1 panel-transition animate-fade-in">
              {/* Header with back button */}
              <div className="flex justify-between items-center mb-4 md:mb-6 flex-wrap gap-2">
                {phoneCallMode ? (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={togglePhoneCallMode}
                    className="w-10 py-2 px-2 text-[var(--foreground)] hover:text-[var(--foreground)]/80 flex items-center justify-center border border-[var(--border)] rounded-full group overflow-hidden hover:w-auto transition-all duration-300"
                  >
                    <IconWrapper icon={IoArrowBack} className="flex-shrink-0" />
                    <span className="max-w-0 overflow-hidden group-hover:max-w-xs group-hover:ml-2 transition-all duration-300 ease-in-out text-sm md:text-base whitespace-nowrap">
                      Back to Web Call
                    </span>
                  </motion.button>
                ) : (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleBack}
                    className="w-10 py-2 px-2 text-[var(--foreground)] hover:text-[var(--foreground)]/80 flex items-center justify-center border border-[var(--border)] rounded-full group overflow-hidden hover:w-auto transition-all duration-300"
                  >
                    <IconWrapper icon={IoArrowBack} className="flex-shrink-0" />
                    <span className="max-w-0 overflow-hidden group-hover:max-w-xs group-hover:ml-2 transition-all duration-300 ease-in-out text-sm md:text-base whitespace-nowrap">
                      Back to Agents
                    </span>
                  </motion.button>
                )}

                {!phoneCallMode && (
                  <div className="flex items-center gap-2 md:gap-4 flex-nowrap justify-end">
                    {/* Share button */}
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleShareAgent}
                      className="bg-[var(--card)] px-3 py-1.5 font-semibold rounded-full text-sm font-medium hover:bg-[var(--card)]/80 transition-colors flex items-center gap-2 border border-[var(--border)] shadow-sm whitespace-nowrap"
                    >
                      {linkCopied ? (
                        <>
                          <HiCheck className="w-4 h-4 text-green-500" />
                          <span className="text-green-500">Copied!</span>
                        </>
                      ) : (
                        <>
                          <HiOutlineShare className="w-4 h-4" />
                          <span>Share Link</span>
                        </>
                      )}
                    </motion.button>

                    {/* Session Timer Display */}
                    <TimerDisplay />

                    <button
                      onClick={toggleCostModal}
                      className="bg-[var(--card)] px-3 py-1.5 font-semibold rounded-full text-sm font-medium hover:bg-[var(--card)]/80 transition-colors flex items-center gap-2 border border-[var(--border)] shadow-sm whitespace-nowrap"
                    >
                      <span className="text-[var(--foreground)]">
                        ${calculateDetailedCosts().grandTotalUsd.toFixed(2)} / ₹
                        {calculateDetailedCosts().grandTotalInr.toFixed(2)}
                      </span>
                    </button>
                  </div>
                )}
              </div>

              {/* Main content area */}
              <div className="flex flex-col items-center">
                {!phoneCallMode ? (
                  <>
                    {/* Standard Audio Agent UI */}
                    {/* Visualizer */}
                    <div
                      className={`w-full max-w-md aspect-square mx-auto mb-4 md:mb-8 relative ${
                        audioActive && isConnected ? "active-visualizer" : ""
                      }`}
                    >
                      <div className={`absolute inset-0 rounded-full`}></div>
                      <VoiceVisualizer
                        audioStream={outputStream}
                        isActive={audioActive && isConnected}
                      />
                    </div>
                    {/* Error message */}
                    {errorMessage && (
                      <div className="bg-red-500/10 text-red-400 px-4 py-2 rounded-lg text-sm w-full max-w-md text-center mb-4">
                        {errorMessage}
                      </div>
                    )}
                    {/* No uses remaining message */}
                    {!isAdmin &&
                      remainingUses !== null &&
                      remainingUses <= 0 && (
                        <div className="bg-amber-500/10 text-amber-500 px-4 py-3 rounded-lg text-sm w-full max-w-md text-center mb-4 border border-amber-500/20 animate-pulse-slow">
                          <p className="font-medium mb-1">
                            You have no uses remaining
                          </p>
                          <p>
                            Please contact{" "}
                            <a
                              href="mailto:contact@ukti.ai"
                              className="underline font-medium"
                            >
                              contact@ukti.ai
                            </a>{" "}
                            to request more.
                          </p>
                        </div>
                      )}
                    {/* Mic button */}
                    <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-6 md:mb-8">
                      {!stream ? (
                        <button
                          className={`bg-[var(--secondary)] hover:bg-[var(--secondary-hover)] text-[var(--secondary-foreground)] px-4 md:px-6 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm md:text-base button-transition ${
                            !isAdmin &&
                            remainingUses !== null &&
                            remainingUses <= 0
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          onClick={setupMicrophone}
                          disabled={
                            !isAdmin &&
                            remainingUses !== null &&
                            remainingUses <= 0
                          }
                          hidden={
                            !isAdmin &&
                            remainingUses !== null &&
                            remainingUses <= 0
                          }
                        >
                          <IconWrapper icon={BsMicFill} /> Enable Microphone
                        </button>
                      ) : isConnected ? (
                        <>
                          <button
                            className="bg-red-500 hover:bg-red-600 text-white px-4 md:px-6 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm md:text-base button-transition"
                            onClick={disconnectFromAgent}
                          >
                            Disconnect
                          </button>
                          <button
                            className={`border ${
                              isMuted
                                ? "bg-[var(--secondary)] text-[var(--secondary-foreground)]"
                                : "bg-[var(--muted)] text-[var(--muted-foreground)]"
                            } px-4 md:px-6 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm md:text-base button-transition ${
                              !isMuted && isConnected
                                ? "animate-pulse-slow"
                                : ""
                            }`}
                            onClick={toggleMute}
                          >
                            {isMuted ? (
                              <>
                                <IconWrapper icon={BsMicFill} /> Unmute
                              </>
                            ) : (
                              <>
                                <IconWrapper icon={BsMicMuteFill} /> Mute
                              </>
                            )}
                          </button>
                        </>
                      ) : (
                        <button
                          className={`bg-[var(--secondary)] hover:bg-[var(--secondary-hover)] text-[var(--secondary-foreground)] px-4 md:px-6 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm md:text-base button-transition ${
                            !isAdmin &&
                            remainingUses !== null &&
                            remainingUses <= 0
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                          onClick={connectToAgent}
                          disabled={
                            isConnecting ||
                            (!isAdmin &&
                              remainingUses !== null &&
                              remainingUses <= 0)
                          }
                          hidden={
                            !isAdmin &&
                            remainingUses !== null &&
                            remainingUses <= 0
                          }
                        >
                          {isConnecting ? "Connecting..." : "Connect"}
                        </button>
                      )}

                      {/* Phone Call button - only show if agent has phone call capability and mic is not enabled */}
                      {agent?.phone_supported && !stream && isAdmin && (
                        <motion.button
                          whileHover={{
                            scale:
                              !isAdmin &&
                              remainingUses !== null &&
                              remainingUses <= 0
                                ? 1
                                : 1.02,
                          }}
                          whileTap={{
                            scale:
                              !isAdmin &&
                              remainingUses !== null &&
                              remainingUses <= 0
                                ? 1
                                : 0.98,
                          }}
                          onClick={togglePhoneCallMode}
                          disabled={
                            !isAdmin &&
                            remainingUses !== null &&
                            remainingUses <= 0
                          }
                          hidden={
                            !isAdmin ||
                            (remainingUses !== null && remainingUses <= 0)
                          }
                          className={`bg-[var(--secondary)] text-[var(--secondary-foreground)] hover:bg-[var(--secondary-hover)] px-4 md:px-6 py-2.5 rounded-lg flex items-center gap-2 transition-colors text-sm md:text-base font-medium ${
                            !isAdmin ||
                            (remainingUses !== null && remainingUses <= 0)
                              ? "opacity-50 cursor-not-allowed hidden"
                              : ""
                          }`}
                        >
                          <HiPhone className="w-5 h-5" />
                          <span>Call Agent</span>
                        </motion.button>
                      )}
                    </div>

                    {/* Info panels */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 w-full">
                      {/* Limitations panel */}
                      <div className="bg-[var(--card)] rounded-lg border border-[var(--border)] p-3 md:p-4 panel-transition hover-scale">
                        <h3 className="text-[var(--foreground)] font-medium text-base md:text-lg mb-2 md:mb-3">
                          Current limitations
                        </h3>
                        {agent &&
                        agent.limitations &&
                        agent.limitations.length > 0 ? (
                          <ul className="space-y-1.5 md:space-y-2">
                            {agent.limitations.map((limitation, index) => (
                              <li
                                key={index}
                                className="flex items-start gap-2 animate-slide-up"
                                style={{ animationDelay: `${index * 0.1}s` }}
                              >
                                <span className="text-[var(--foreground)] text-lg leading-none">
                                  •
                                </span>
                                <span className="text-[var(--foreground)] -mt-1 text-sm md:text-base">
                                  {limitation}
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-[var(--foreground)] italic text-sm md:text-base">
                            No limitations specified
                          </p>
                        )}
                      </div>

                      <div className="bg-[var(--card)] rounded-lg border border-[var(--border)] p-3 md:p-4 panel-transition hover-scale">
                        <div className="flex flex-col space-y-3">
                          {/* Agent Type */}
                          <div className="flex items-center justify-between">
                            <span className="text-[var(--foreground)] text-xs md:text-sm">
                              Agent Type:
                            </span>
                            <span
                              className={`px-2 md:px-3 py-1 rounded-full text-xs md:text-sm ${
                                agent?.is_outbound
                                  ? "bg-blue-900 text-blue-200"
                                  : "bg-purple-900 text-purple-200"
                              }`}
                            >
                              {agent?.is_outbound ? "Outbound" : "Inbound"}
                            </span>
                          </div>

                          {/* Languages */}
                          <div className="space-y-2 md:space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-[var(--foreground)] text-xs md:text-sm">
                                Main Language:
                              </span>
                              <span className="text-[var(--foreground)] text-xs md:text-sm px-2 py-0.5 bg-[var(--secondary)] text-[var(--secondary-foreground)] rounded">
                                {agent &&
                                  getLanguageName(agent.primary_language)}
                              </span>
                            </div>
                            <div>
                              <div className="flex flex-wrap gap-1.5 justify-between">
                                <span className="text-[var(--foreground)] text-xs md:text-sm">
                                  Also speaks:
                                </span>
                                <div className="flex flex-wrap gap-1.5">
                                  {agent &&
                                    agent.supported_languages
                                      .filter(
                                        (lang) =>
                                          lang !== agent.primary_language
                                      )
                                      .map((lang, index) => (
                                        <span
                                          key={index}
                                          className="text-[var(--secondary-foreground)] text-xs px-2 py-1 bg-[var(--secondary)] rounded"
                                        >
                                          {getLanguageName(lang)}
                                        </span>
                                      ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  /* Phone Call Mode UI with back button inside the card */
                  <div className="flex flex-col items-center w-full">
                    <div className="w-full max-w-md mx-auto bg-[var(--card)] p-6 rounded-lg border border-[var(--border)] shadow-md animate-slide-up">
                      <h3 className="text-2xl md:text-3xl font-display font-medium text-[var(--foreground)] text-center mb-6 flex items-center justify-center gap-2">
                        <HiPhone className="w-8 h-8 text-[var(--secondary)]" />
                        Call Voice Agent
                      </h3>

                      {/* No uses remaining message in phone call mode */}
                      {!isAdmin &&
                        remainingUses !== null &&
                        remainingUses <= 0 && (
                          <div className="bg-amber-500/10 text-amber-500 px-4 py-3 rounded-lg text-sm mb-6 border border-amber-500/20 animate-pulse-slow">
                            <p className="font-medium mb-1 text-center">
                              You have no uses remaining
                            </p>
                            <p className="text-center">
                              Please contact{" "}
                              <a
                                href="mailto:contact@ukti.ai"
                                className="underline font-medium"
                              >
                                contact@ukti.ai
                              </a>{" "}
                              to request more.
                            </p>
                          </div>
                        )}

                      <form
                        onSubmit={handlePhoneCallSubmit}
                        className="space-y-6"
                      >
                        <div>
                          <label
                            htmlFor="phone"
                            className="block text-sm font-medium text-[var(--foreground)] mb-2"
                          >
                            Your Phone Number
                          </label>
                          <PhoneInput
                            id="phone"
                            value={phoneNumber}
                            onChange={handlePhoneNumberChange}
                            onBlur={validatePhoneNumber}
                            placeholder={
                              phoneNumberPlaceholder
                                ? phoneNumberPlaceholder
                                : "Enter your Indian phone number"
                            }
                            disabled={
                              isPhoneCallProcessing ||
                              (!isAdmin &&
                                remainingUses !== null &&
                                remainingUses <= 0)
                            }
                            className={`w-full ${
                              !isAdmin &&
                              remainingUses !== null &&
                              remainingUses <= 0
                                ? "opacity-60 cursor-not-allowed"
                                : ""
                            }`}
                            error={!!phoneValidationMsg}
                            autoComplete="tel"
                          />
                          {phoneValidationMsg && (
                            <p className="mt-2 text-sm text-red-500 animate-fade-in">
                              {phoneValidationMsg}
                            </p>
                          )}
                        </div>

                        <div>
                          <motion.button
                            type="submit"
                            disabled={
                              isPhoneCallProcessing ||
                              (!isAdmin &&
                                remainingUses !== null &&
                                remainingUses <= 0)
                            }
                            hidden={
                              !isAdmin &&
                              remainingUses !== null &&
                              remainingUses <= 0
                            }
                            whileHover={{
                              scale:
                                (!isAdmin &&
                                  remainingUses !== null &&
                                  remainingUses <= 0) ||
                                isPhoneCallProcessing
                                  ? 1
                                  : 1.02,
                            }}
                            whileTap={{
                              scale:
                                (!isAdmin &&
                                  remainingUses !== null &&
                                  remainingUses <= 0) ||
                                isPhoneCallProcessing
                                  ? 1
                                  : 0.98,
                            }}
                            className={`w-full bg-[var(--secondary)] text-[var(--secondary-foreground)] px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-colors text-sm md:text-base font-medium ${
                              isPhoneCallProcessing ||
                              (!isAdmin &&
                                remainingUses !== null &&
                                remainingUses <= 0)
                                ? "opacity-50 cursor-not-allowed"
                                : "hover:bg-[var(--secondary-hover)]"
                            }`}
                          >
                            {isPhoneCallProcessing ? (
                              <>
                                <motion.span
                                  animate={{ rotate: 360 }}
                                  transition={{
                                    duration: 1,
                                    repeat: Infinity,
                                    ease: "linear",
                                  }}
                                  className="inline-block w-5 h-5 border-2 border-t-2 border-white/30 border-t-white rounded-full"
                                ></motion.span>
                                Processing...
                              </>
                            ) : (
                              <>
                                <HiPhone className="w-5 h-5" />
                                Call Now
                              </>
                            )}
                          </motion.button>
                        </div>
                      </form>

                      {/* Success/Error Message */}
                      {phoneCallMessage && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            type: "spring",
                            stiffness: 400,
                            damping: 25,
                          }}
                          className="mt-6 rounded-lg text-sm overflow-hidden shadow-md font-sans"
                        >
                          <div
                            className={`p-1 ${
                              phoneCallMessage.type === "success"
                                ? "bg-[var(--color-accent)]"
                                : "bg-[var(--destructive)]"
                            }`}
                          ></div>
                          <div
                            className={`p-4 ${
                              phoneCallMessage.type === "success"
                                ? "bg-[var(--card)] text-[var(--foreground)]"
                                : "bg-[var(--card)] text-[var(--foreground)]"
                            }`}
                          >
                            <div className="flex items-start">
                              <div className="flex-shrink-0 mr-3">
                                {phoneCallMessage.type === "success" ? (
                                  <motion.div
                                    initial={{ scale: 0.5, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{
                                      type: "spring",
                                      stiffness: 400,
                                      damping: 10,
                                      delay: 0.1,
                                    }}
                                    className="w-8 h-8 rounded-full bg-[var(--color-accent)]/20 flex items-center justify-center"
                                  >
                                    <svg
                                      className="w-5 h-5 text-[var(--color-accent)]"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 13l4 4L19 7"
                                      />
                                    </svg>
                                  </motion.div>
                                ) : (
                                  <motion.div
                                    initial={{ scale: 0.5, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{
                                      type: "spring",
                                      stiffness: 400,
                                      damping: 10,
                                      delay: 0.1,
                                    }}
                                    className="w-8 h-8 rounded-full bg-[var(--destructive)]/20 flex items-center justify-center"
                                  >
                                    <svg
                                      className="w-5 h-5 text-[var(--destructive)]"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                      />
                                    </svg>
                                  </motion.div>
                                )}
                              </div>
                              <div>
                                <h3 className="font-display font-medium mb-1 text-[var(--foreground)]">
                                  {phoneCallMessage.type === "success"
                                    ? "Call Initiated Successfully"
                                    : "Call Initiation Failed"}
                                </h3>
                                <p className="text-[var(--foreground-muted)]">
                                  {phoneCallMessage.text}
                                </p>

                                {phoneCallMessage.type === "success" && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    transition={{ delay: 0.3 }}
                                    className="mt-2 pt-2 border-t border-[var(--border)]"
                                  >
                                    <div className="flex items-center text-xs text-[var(--foreground-muted)]">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 mr-1 text-[var(--color-accent)]"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                      </svg>
                                      <span>
                                        You should receive a call shortly
                                      </span>
                                    </div>
                                    <div className="flex items-center text-xs mt-1 text-[var(--foreground-muted)]">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 mr-1 text-[var(--color-accent)]"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                      </svg>
                                      <span>
                                        Keep your phone ready to answer
                                      </span>
                                    </div>
                                  </motion.div>
                                )}

                                {phoneCallMessage.type === "error" && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    transition={{ delay: 0.3 }}
                                    className="mt-2 pt-2 border-t border-[var(--border)]"
                                  >
                                    <button
                                      onClick={() => {
                                        setPhoneCallMessage(null);
                                        // Focus on the phone input after a short delay
                                        setTimeout(() => {
                                          const phoneInput =
                                            document.getElementById("phone");
                                          if (phoneInput) phoneInput.focus();
                                        }, 100);
                                      }}
                                      className="text-xs px-3 py-1 bg-[var(--secondary)] text-[var(--secondary-foreground)] rounded transition-colors hover:bg-[var(--secondary-hover)]"
                                    >
                                      Try Again
                                    </button>
                                  </motion.div>
                                )}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}

                      {/* Info note - enhanced */}
                      <div className="mt-6 text-sm text-[var(--foreground)]/70 p-4 border border-[var(--border)]/40 rounded-lg bg-[var(--background)]/50">
                        <p className="flex items-center gap-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-[var(--accent)]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          The agent will call you at the phone number provided.
                          Standard call charges may apply.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Live Chat Panel - Only show when not in phone call mode */}
            {!phoneCallMode && (
              <div
                className={`bg-[var(--card-container)] border border-[var(--border)] shadow-lg transition-all duration-500 panel-transition animate-fade-in
                  ${
                    isMobile
                      ? "fixed bottom-0 left-0 right-0 z-50 rounded-t-3xl transform transition-transform duration-300"
                      : "lg:w-96 w-full lg:static rounded-3xl"
                  }`}
                style={{
                  transform:
                    isMobile && chatExpanded
                      ? "translateY(0)"
                      : isMobile && !chatExpanded
                      ? "translateY(85%)"
                      : "none",
                  maxHeight:
                    isMobile && chatExpanded
                      ? "80vh"
                      : isMobile && !chatExpanded
                      ? "30vh"
                      : "auto",
                }}
                onTouchStart={isMobile ? handleTouchStart : undefined}
                onTouchMove={isMobile ? handleTouchMove : undefined}
                onTouchEnd={isMobile ? handleTouchEnd : undefined}
              >
                {/* Chat header */}
                <div className="flex items-center justify-between p-3 border-b border-[var(--border)]">
                  <h3 className="text-[var(--foreground)] font-medium text-base">
                    Live Chat
                  </h3>

                  {/* Swipe indicator - only on mobile */}
                  {isMobile && (
                    <div
                      className="absolute left-1/2 transform -translate-x-1/2 top-0 w-full flex flex-col items-center justify-center pt-3 pb-2 cursor-pointer"
                      onClick={() => setChatExpanded(!chatExpanded)}
                    >
                      <div className="h-1 w-16 bg-gray-300 rounded-full mb-1"></div>
                      <div
                        className={`transition-transform duration-300 ${
                          chatExpanded ? "rotate-180" : ""
                        }`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 text-gray-400 animate-arrow-bounce"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2">
                    {/* Only show close button on mobile */}
                    {isMobile && (
                      <button
                        onClick={() => setShowChat(false)}
                        className="text-[var(--foreground)] hover:text-[var(--foreground)] transition-colors p-1 button-transition"
                      >
                        <IconWrapper icon={IoMdClose} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Chat messages container */}
                <div
                  ref={chatContainerRef}
                  className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-3 md:space-y-4 
                    min-h-[250px] max-h-[350px] lg:min-h-[350px] lg:max-h-[500px]"
                >
                  {chatMessages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 animate-fade-in">
                      <HiOutlineChat className="w-10 h-10 md:w-12 md:h-12 mb-3 md:mb-4 text-[var(--foreground)] animate-bounce-slow" />
                      <p className="text-[var(--foreground)] text-sm md:text-base">
                        No messages yet
                      </p>
                      <p className="text-xs md:text-sm mt-2 text-[var(--foreground)]">
                        Start speaking to see the chat history
                      </p>
                    </div>
                  ) : (
                    chatMessages.map((message, index) =>
                      message.type === "user" ? (
                        <div
                          key={index}
                          className="bg-[var(--color-secondary)] p-3 rounded-lg shadow-sm max-w-[85%] text-[var(--secondary-foreground)] animate-slide-up"
                          style={{ animationDelay: `${index * 0.05}s` }}
                        >
                          <div className="font-medium text-xs md:text-sm mb-1 flex items-center justify-between">
                            <div className="flex items-center">
                              <span className="bg-[var(--background)] p-1 rounded-full mr-2">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-3 w-3 md:h-3.5 md:w-3.5 text-[var(--color-secondary)]"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                  />
                                </svg>
                              </span>
                              <span>You</span>
                            </div>
                            <span className="text-xs opacity-80">
                              {formatMessageTime(message.timestamp)}
                            </span>
                          </div>
                          <div className="font-medium text-sm md:text-base">
                            {message.text}
                          </div>
                        </div>
                      ) : (
                        <div
                          key={index}
                          className="bg-[var(--card)] p-3 rounded-lg ml-auto mr-0 shadow-sm max-w-[85%] text-[var(--card-foreground)] animate-slide-up"
                          style={{ animationDelay: `${index * 0.05}s` }}
                        >
                          <div className="font-medium text-xs md:text-sm mb-1 flex items-center justify-between">
                            <div className="flex items-center">
                              <span className="bg-[var(--background)] p-1 rounded-full mr-2">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-3 w-3 md:h-3.5 md:w-3.5 text-[var(--foreground)]"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                                  />
                                </svg>
                              </span>
                              <span>Agent</span>
                            </div>
                            <span className="text-xs opacity-80">
                              {formatMessageTime(message.timestamp)}
                            </span>
                          </div>
                          <div className="font-medium text-sm md:text-base">
                            {message.text}
                          </div>
                        </div>
                      )
                    )
                  )}
                </div>
              </div>
            )}

            {/* Chat toggle button - only on mobile and only when chat is closed and not in phone call mode */}
            {/* {!showChat && isMobile && !phoneCallMode && (
              <button
                onClick={() => {
                  setShowChat(true);
                  setChatExpanded(true); // Open directly to maximized state
                }}
                className="fixed bottom-4 right-4 z-50 bg-[var(--secondary)] text-[var(--secondary-foreground)] rounded-full w-12 h-12 flex items-center justify-center shadow-lg hover:bg-[var(--secondary-hover)] transition-colors button-transition animate-pulse-slow"
              >
                <HiOutlineChat className="w-6 h-6" />
                {chatMessages.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold animate-bounce-slow">
                    {chatMessages.length > 9 ? "9+" : chatMessages.length}
                  </span>
                )}
              </button>
            )} */}
          </div>

          {/* Cost Breakdown Modal */}
          {isAdmin && showCostModal && (
            <Modal
              isOpen={showCostModal}
              onClose={() => setShowCostModal(false)}
              title="Call Cost Breakdown"
            >
              <div className="bg-[var(--card)] text-[var(--foreground)] rounded-lg p-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
                <div className="w-full max-w-3xl mx-auto">
                  <div className="bg-[var(--card)] p-4 rounded-lg border border-[var(--border)] mb-6">
                    <h3 className="text-[var(--foreground)] font-semibold mb-3">
                      Session Info
                    </h3>
                    <div className="flex justify-between text-sm">
                      <p className="text-[var(--foreground)]">Duration: </p>
                      <SessionDurationDisplay />
                    </div>
                    <div className="flex justify-between text-sm mt-2">
                      <p className="text-[var(--foreground)]">Total Tokens: </p>
                      <p className="text-[var(--foreground)] font-medium">
                        {(
                          tokenUsage.input_tokens + tokenUsage.output_tokens
                        ).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex justify-between text-sm mt-2">
                      <p className="text-[var(--foreground)]">
                        Per Minute Cost:{" "}
                      </p>
                      <p className="text-[var(--foreground)] font-medium">
                        ${calculateDetailedCosts().perMinuteUsdCost.toFixed(4)}{" "}
                        / ₹
                        {calculateDetailedCosts().perMinuteInrCost.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  <div className="bg-[var(--card)] p-4 rounded-lg border border-[var(--border)] mb-6">
                    <h3 className="text-[var(--foreground)] font-semibold mb-3">
                      Token Usage Summary
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-[var(--foreground)]">
                          Input Tokens:{" "}
                          <span className="text-[var(--foreground)] font-medium">
                            {tokenUsage.input_tokens.toLocaleString()}
                          </span>
                        </p>
                        <div className="ml-4 mt-1">
                          <p className="text-[var(--foreground)]">
                            Text:{" "}
                            <span className="text-[var(--foreground)]">
                              {(
                                tokenUsage.input_token_details.text || 0
                              ).toLocaleString()}
                            </span>
                          </p>
                          <p className="text-[var(--foreground)]">
                            Audio:{" "}
                            <span className="text-[var(--foreground)]">
                              {(
                                tokenUsage.input_token_details.audio || 0
                              ).toLocaleString()}
                            </span>
                          </p>
                          <p className="text-[var(--foreground)]">
                            Cached:{" "}
                            <span className="text-[var(--foreground)]">
                              {(
                                tokenUsage.input_token_details.cached || 0
                              ).toLocaleString()}
                            </span>
                          </p>
                          <p className="text-[var(--foreground)] ml-4">
                            Cached Text:{" "}
                            <span className="text-[var(--foreground)]">
                              {(
                                tokenUsage.input_token_details.cached_text || 0
                              ).toLocaleString()}
                            </span>
                          </p>
                          <p className="text-[var(--foreground)] ml-4">
                            Cached Audio:{" "}
                            <span className="text-[var(--foreground)]">
                              {(
                                tokenUsage.input_token_details.cached_audio || 0
                              ).toLocaleString()}
                            </span>
                          </p>
                        </div>
                      </div>
                      <div>
                        <p className="text-[var(--foreground)]">
                          Output Tokens:{" "}
                          <span className="text-[var(--foreground)] font-medium">
                            {tokenUsage.output_tokens.toLocaleString()}
                          </span>
                        </p>
                        <div className="ml-4 mt-1">
                          <p className="text-[var(--foreground)]">
                            Text:{" "}
                            <span className="text-[var(--foreground)]">
                              {(
                                tokenUsage.output_token_details.text || 0
                              ).toLocaleString()}
                            </span>
                          </p>
                          <p className="text-[var(--foreground)]">
                            Audio:{" "}
                            <span className="text-[var(--foreground)]">
                              {(
                                tokenUsage.output_token_details.audio || 0
                              ).toLocaleString()}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[var(--card)] p-4 rounded-lg border border-[var(--border)] mb-6">
                    <h3 className="text-[var(--foreground)] font-semibold mb-3">
                      Cost Breakdown
                    </h3>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left pb-2 font-medium text-gray-600">
                            Type
                          </th>
                          <th className="text-right pb-2 font-medium text-gray-600">
                            Rate
                          </th>
                          <th className="text-right pb-2 font-medium text-gray-600">
                            Tokens/Units
                          </th>
                          <th className="text-right pb-2 font-medium text-gray-600">
                            Cost
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-gray-100">
                          <td className="py-2 text-gray-700">Input Text</td>
                          <td className="py-2 text-right text-gray-700">
                            ${COST_CONSTANTS.GEMINI_INPUT_TEXT.toFixed(2)}/1M
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            {(
                              tokenUsage.input_token_details.text || 0
                            ).toLocaleString()}
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            ${calculateDetailedCosts().textInputCost.toFixed(4)}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="py-2 text-gray-700">Input Audio</td>
                          <td className="py-2 text-right text-gray-700">
                            ${COST_CONSTANTS.GEMINI_INPUT_AUDIO.toFixed(2)}/1M
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            {(
                              tokenUsage.input_token_details.audio || 0
                            ).toLocaleString()}
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            $
                            {calculateDetailedCosts().audioInputCost.toFixed(4)}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="py-2 text-gray-700">Output Text</td>
                          <td className="py-2 text-right text-gray-700">
                            ${COST_CONSTANTS.GEMINI_OUTPUT_TEXT.toFixed(2)}/1M
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            {(
                              tokenUsage.output_token_details.text || 0
                            ).toLocaleString()}
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            $
                            {calculateDetailedCosts().textOutputCost.toFixed(4)}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="py-2 text-gray-700">Output Audio</td>
                          <td className="py-2 text-right text-gray-700">
                            ${COST_CONSTANTS.GEMINI_OUTPUT_AUDIO.toFixed(2)}/1M
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            {(
                              tokenUsage.output_token_details.audio || 0
                            ).toLocaleString()}
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            $
                            {calculateDetailedCosts().audioOutputCost.toFixed(
                              4
                            )}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="py-2 text-gray-700">
                            Fixed Connection Cost
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            ₹{COST_CONSTANTS.FIXED_CONNECTION_COST.toFixed(2)}
                          </td>
                          <td className="py-2 text-right text-gray-700">1</td>
                          <td className="py-2 text-right text-gray-700">
                            ₹{calculateDetailedCosts().fixedCost.toFixed(2)}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="py-2 text-gray-700">
                            Calling Service
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            ₹
                            {COST_CONSTANTS.CALLING_SERVICE_COST_PER_MIN.toFixed(
                              2
                            )}
                            /min
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            {callDurationMinutes} min
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            ₹
                            {calculateDetailedCosts().callingServiceCost.toFixed(
                              2
                            )}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100">
                          <td className="py-2 text-gray-700">Service Charge</td>
                          <td className="py-2 text-right text-gray-700">
                            ₹{COST_CONSTANTS.SERVICE_CHARGE_PER_MIN.toFixed(2)}
                            /min
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            {callDurationMinutes} min
                          </td>
                          <td className="py-2 text-right text-gray-700">
                            ₹{calculateDetailedCosts().serviceCharge.toFixed(2)}
                          </td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr className="border-t border-gray-200">
                          <td
                            colSpan={3}
                            className="pt-3 text-gray-900 font-medium"
                          >
                            API Cost (USD)
                          </td>
                          <td className="pt-3 text-right text-gray-900 font-medium">
                            ${calculateDetailedCosts().totalUsdCost.toFixed(4)}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3} className="text-gray-900 font-medium">
                            API Cost (INR) @ ₹{COST_CONSTANTS.USD_TO_INR}/USD
                          </td>
                          <td className="text-right text-gray-900 font-medium">
                            ₹{calculateDetailedCosts().totalInrCost.toFixed(2)}
                          </td>
                        </tr>
                        <tr className="border-t-2 border-gray-300">
                          <td
                            colSpan={3}
                            className="pt-3 text-gray-900 font-bold"
                          >
                            Grand Total (INR)
                          </td>
                          <td className="pt-3 text-right text-gray-900 font-bold">
                            ₹{calculateDetailedCosts().grandTotalInr.toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  <div className="text-xs text-gray-500 mb-4">
                    <p className="mb-1">Notes:</p>
                    <ul className="list-disc ml-4 space-y-1">
                      <li>
                        Costs are calculated based on current pricing for
                        Ukti.ai
                      </li>
                      <li>
                        Fixed connection cost (₹
                        {COST_CONSTANTS.FIXED_CONNECTION_COST}) is applied once
                        per session
                      </li>
                      <li>
                        Calling service charges (₹
                        {COST_CONSTANTS.CALLING_SERVICE_COST_PER_MIN}/min) are
                        billed by rounding up to the next minute
                      </li>
                      <li>
                        Service charge (₹{COST_CONSTANTS.SERVICE_CHARGE_PER_MIN}
                        /min) is applied for each minute
                      </li>
                      <li>
                        USD to INR conversion fixed at rate of ₹
                        {COST_CONSTANTS.USD_TO_INR}/USD
                      </li>
                      <li>
                        This is a live counter that updates with each agent
                        response
                      </li>
                    </ul>
                  </div>

                  <div className="mt-6 flex justify-end gap-4">
                    <button
                      onClick={resetCostCounter}
                      className="px-4 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors flex items-center"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 mr-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      Reset Counter
                    </button>
                    <button
                      onClick={() => setShowCostModal(false)}
                      className="px-4 py-2 bg-[var(--secondary)] text-[var(--secondary-foreground)] rounded hover:bg-[var(--secondary-hover)] transition-colors flex items-center"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 mr-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </Modal>
          )}
        </div>
      </div>
      <audio ref={audioRef} autoPlay playsInline style={{ display: "none" }} />

      {/* Phone Call Warning */}
      {showPhoneCallWarning && (
        <Modal
          isOpen={showPhoneCallWarning}
          onClose={() => setShowPhoneCallWarning(false)}
          title="Phone Call Warning"
        >
          <div className="p-4">
            <p className="mb-4">
              This will initiate a phone call to the number you provide.
              Standard call charges may apply.
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowPhoneCallWarning(false)}
                className="px-4 py-2 bg-[var(--secondary)] text-[var(--secondary-foreground)] rounded"
              >
                OK
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Out of Uses Modal */}
      {showOutOfUsesModal && (
        <Modal
          isOpen={showOutOfUsesModal}
          onClose={() => {
            setShowOutOfUsesModal(false);
            // Redirect after closing the modal
            if (onBackToSelection) {
              onBackToSelection();
            } else {
              navigate("/");
            }
          }}
          title="Out of Uses"
        >
          <div className="p-6 text-center">
            <div className="mb-4 flex justify-center">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-10 w-10 text-amber-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            </div>
            <h3 className="text-xl font-medium mb-4">You're out of uses</h3>
            <p className="text-[var(--foreground-muted)] mb-6">
              You have used all your allocated interactions with this voice
              agent. Please contact{" "}
              <a
                href="mailto:contact@ukti.ai"
                className="text-[var(--accent)] font-medium"
              >
                contact@ukti.ai
              </a>{" "}
              to request more.
            </p>
            <div className="flex justify-center">
              <button
                onClick={() => {
                  setShowOutOfUsesModal(false);
                  // Redirect after closing the modal
                  if (onBackToSelection) {
                    onBackToSelection();
                  } else {
                    navigate("/");
                  }
                }}
                className="px-6 py-2.5 bg-[var(--secondary)] text-[var(--secondary-foreground)] rounded-md font-medium"
              >
                Return to Dashboard
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Custom scrollbar styles */}
      <style>
        {`
          .custom-scrollbar::-webkit-scrollbar {
            width: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: var(--card-container);
            border-radius: 4px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: var(--border);
            border-radius: 4px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: var(--color-secondary);
          }

          /* New animations */
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes slideUp {
            from { transform: translateY(10px); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
          }

          @keyframes pulse {
            0% { box-shadow: 0 0 0 0 rgba(var(--accent-rgb), 0.4); }
            70% { box-shadow: 0 0 0 10px rgba(var(--accent-rgb), 0); }
            100% { box-shadow: 0 0 0 0 rgba(var(--accent-rgb), 0); }
          }
          
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-5px); }
          }

          @keyframes arrowBounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-3px); }
          }

          .animate-arrow-bounce {
            animation: arrowBounce 1.5s ease-in-out infinite;
          }

          .animate-fade-in {
            animation: fadeIn 0.3s ease-in-out;
          }

          .animate-slide-up {
            animation: slideUp 0.4s cubic-bezier(0.18, 0.89, 0.32, 1.28);
            transform-origin: bottom left;
          }

          .animate-pulse-slow {
            animation: pulse 2s infinite;
          }
          
          .animate-bounce-slow {
            animation: bounce 2s infinite;
          }

          .hover-scale {
            transition: transform 0.2s ease-out;
          }
          
          .hover-scale:hover {
            transform: scale(1.02);
          }

          .panel-transition {
            transition: all 0.3s ease-out;
          }
          
          .button-transition {
            transition: all 0.15s ease-out;
          }
          
          .button-transition:hover {
            transform: translateY(-1px);
            filter: brightness(1.05);
          }

          .chat-message-transition {
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }

          /* Visualizer effects */
          .active-visualizer {
            transition: transform 0.3s ease-out;
          }
          
          .visualizer-glow {
            animation: glow 3s ease-in-out infinite alternate;
            border-radius: 50%;
          }
          
          @keyframes glow {
            0% {
              box-shadow: 0 0 15px 5px rgba(var(--accent-rgb), 0.15);
            }
            100% {
              box-shadow: 0 0 25px 10px rgba(var(--accent-rgb), 0.25);
            }
          }
          
          /* Add smooth entry animation for the visualizer */
          .active-visualizer {
            animation: scaleIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          }
          
          @keyframes scaleIn {
            0% {
              transform: scale(0.95);
              opacity: 0.7;
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
};

export default AudioAgent;
